import axios from 'axios';
import { getBackendUrl } from '@/core/config/backendUrl';
import { endSession } from '@/core/auth/sessionExpiry';
import { getAccessToken } from '@/core/auth/tokenStore';

/**
 * Dedicated Axios instance for module API helpers.
 * Reproduces historical base URL + bearer behavior without retries or error wrapping.
 * Page-local axios may coexist; AuthContext keeps both defaults and this instance in sync.
 */
export const httpClient = axios.create();

/** In-memory token for the instance; AuthContext updates this. Falls back to sessionStorage. */
let authToken = null;

/**
 * Sync bearer used by the dedicated client (and optionally keep sessionStorage as fallback).
 * Pass null to clear (logout).
 */
export function setHttpAuthToken(token) {
  authToken = token || null;
  if (token) {
    httpClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete httpClient.defaults.headers.common.Authorization;
  }
}

function resolveToken() {
  if (authToken) return authToken;
  const stored = getAccessToken();
  return stored || null;
}

function hasAuthorizationHeader(headers) {
  if (!headers) return false;
  if (typeof headers.get === 'function') {
    const v = headers.get('Authorization') ?? headers.get('authorization');
    return v != null && v !== '';
  }
  const keys = Object.keys(headers);
  return keys.some((k) => k.toLowerCase() === 'authorization' && headers[k] != null && headers[k] !== '');
}

function clearAuthorizationHeader(headers) {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
    return;
  }
  delete headers.Authorization;
  delete headers.authorization;
}

httpClient.interceptors.request.use((config) => {
  // Always resolve base URL at request time (env / origin can differ per build).
  config.baseURL = getBackendUrl();

  if (config.skipAuth) {
    clearAuthorizationHeader(config.headers);
    return config;
  }

  if (!hasAuthorizationHeader(config.headers)) {
    const token = resolveToken();
    if (token) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const cfg = error?.config || {};
    if (status === 401 && !cfg.skipAuth && resolveToken()) {
      endSession('unauthorized');
    }
    return Promise.reject(error);
  },
);

function request(method, url, data, config = {}) {
  return httpClient.request({
    ...config,
    method,
    url,
    data,
  });
}

export function apiGet(url, config) {
  return httpClient.get(url, config);
}

export function apiPost(url, data, config) {
  return request('post', url, data, config);
}

export function apiPut(url, data, config) {
  return request('put', url, data, config);
}

export function apiPatch(url, data, config) {
  return request('patch', url, data, config);
}

export function apiDelete(url, config) {
  return httpClient.delete(url, config);
}

/** Absolute asset/URL helper using the same backend base as the client. */
export function apiAssetUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const base = getBackendUrl().replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  return `${base}${rel}`;
}
